import { yupResolver } from "@hookform/resolvers/yup";
import type { Item, LinkNextProps as LinkProps } from "@wayflyer/flyui";
import { FormLayout, Link, Modal, Text } from "@wayflyer/flyui";
import { RhfTextField } from "@wayflyer/flyui-hook-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useFormState } from "react-hook-form";
import { useIntl } from "react-intl";

import type { FormSpec, SelectField, ValidatedPasswordField, ValidatedTextField } from "../connector-content/interface";
import { SelectionModal } from "../selection-modal";

import { getYupSchema } from "./validation";
import styles from "./connector-modal.module.css";

type FormatXMLElementFn<T, R = string | T | (string | T)[]> = (parts: Array<string | T>) => R;

interface BaseConnectorModalProps {
  close: () => void;
  isOpen: boolean;
  connectHandler?: (any) => any;
  title?: string;
  link?: Omit<LinkProps, "children"> & { text?: string; linkText?: string };
  showRutterDisclaimer?: boolean;
  showCodatDisclaimer?: boolean;
}

interface _ConnectorModalProps extends BaseConnectorModalProps {
  formFields?: Array<ValidatedTextField | ValidatedPasswordField>;
}

interface _ConnectorSelectionModalProps extends BaseConnectorModalProps {
  formFields: [SelectField];
}

export interface ConnectorModalProps extends BaseConnectorModalProps {
  formFields?: FormSpec;
}

const Disclaimer = ({ content }: { content: React.ReactNode }) => (
  <div className={styles.disclaimer}>
    <Text>{content}</Text>
  </div>
);

const RutterDisclaimer = () => {
  const intl = useIntl();

  const disclaimerMessage = intl.formatMessage(
    {
      id: "onboarding: rutter usage disclaimer",
      defaultMessage:
        "We use a third party service called Rutter to connect to and access your data. For more information, refer to the <link>Rutter Terms and Conditions</link>",
    },
    {
      link: (str: string) => (
        <Link
          url="https://assets.website-files.com/61fa2c9e384336c001748969/628541b11ce364158a0f3e64_Rutter%20Privacy%20Notice.docx%20(1).pdf"
          external
        >
          {str}
        </Link>
      ),
    } as unknown as Record<string, FormatXMLElementFn<string, string>>
  );

  return <Disclaimer content={disclaimerMessage} />;
};

const CodatDisclaimer = () => {
  const intl = useIntl();

  const disclaimerMessage = intl.formatMessage(
    {
      id: "onboarding: codat shopping disclaimer",
      defaultMessage: "We use a third party service called <link>Codat</link> to connect to and access your data",
    },
    {
      link: (str: string) => (
        <Link url="https://www.codat.io/powered-by-codat/" external>
          {str}
        </Link>
      ),
    } as unknown as Record<string, FormatXMLElementFn<string, string>>
  );

  return <Disclaimer content={disclaimerMessage} />;
};

const ConnectorModalInner = ({
  title = "",
  isOpen,
  connectHandler,
  close,
  formFields = [],
  link,
  showRutterDisclaimer = false,
  showCodatDisclaimer = false,
}: _ConnectorModalProps) => {
  const intl = useIntl();
  const formId = "connector-modal-form";
  const schema = getYupSchema(formFields);
  const form = useForm<Record<string, unknown>>({
    resolver: schema ? yupResolver(schema) : undefined,
    mode: "onChange",
  });
  const { isDirty, isValid, isSubmitting } = useFormState({ control: form.control });
  const serverErroredFields = useMemo(() => formFields.filter(({ error }) => !!error), [formFields]);

  const handleFinish = useCallback(
    async (values) => {
      try {
        connectHandler && (await connectHandler(values));
        // eslint-disable-next-line no-empty
      } finally {
      }
    },
    [connectHandler]
  );

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
    return () => form?.reset();
  }, [form, isOpen]);

  useEffect(
    () => serverErroredFields.forEach(({ name, error }) => form.setError(name, { type: "server", message: error })),
    [form, serverErroredFields]
  );

  return (
    <Modal
      title={title}
      primaryAction={{
        content: intl.formatMessage({ id: "shared-ui: Connect", defaultMessage: "Connect" }),
        onAction: form.handleSubmit(handleFinish),
        loading: isSubmitting,
        disabled: !isDirty || !isValid,
      }}
      tertiaryAction={{
        content: intl.formatMessage({ id: "shared-ui: Cancel", defaultMessage: "Cancel" }),
        onAction: close,
      }}
      isOpen={isOpen}
      onClose={close}
      closeOnClickElseWhere={false}
    >
      <form id={formId} onSubmit={form.handleSubmit(handleFinish)}>
        <FormLayout>
          {formFields.map((field) => (
            <RhfTextField form={form} key={field.name} {...field} />
          ))}
        </FormLayout>
      </form>
      {link ? (
        <div className={styles.link}>
          <Text>
            {link.text} <Link {...link}>{link.linkText}</Link>
          </Text>
        </div>
      ) : null}
      {showRutterDisclaimer ? <RutterDisclaimer /> : null}
      {showCodatDisclaimer ? <CodatDisclaimer /> : null}
    </Modal>
  );
};

const ConnectorSelectModal = ({
  title = "",
  isOpen,
  connectHandler,
  close,
  formFields,
}: _ConnectorSelectionModalProps) => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [{ label, options: inOptions }] = formFields;
  const options = inOptions.map(({ value: id, label: text }) => ({ id, text }));

  const handleConfirm = useCallback(
    async (selectedId: string) => {
      const { id: value, text: label } = options.find(({ id }) => id === selectedId) as Item;
      try {
        connectHandler && (await connectHandler({ value, label }));
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    },
    [connectHandler, options]
  );

  return (
    <SelectionModal
      title={title}
      selectLabel={label}
      confirmLabel={intl.formatMessage({ id: "shared-ui: Connect", defaultMessage: "Connect" })}
      isOpen={isOpen}
      options={options}
      close={close}
      onConfirm={handleConfirm}
      onCancel={close}
      isLoading={isLoading}
      closeOnClickElseWhere={false}
    />
  );
};

export const ConnectorModal = ({ formFields, ...rest }: ConnectorModalProps) => {
  const isSelectModal = formFields?.[0]?.type === "select"; // we only support this when it's the only item
  return isSelectModal ? (
    <ConnectorSelectModal formFields={formFields as _ConnectorSelectionModalProps["formFields"]} {...rest} />
  ) : (
    <ConnectorModalInner formFields={formFields as _ConnectorModalProps["formFields"]} {...rest} />
  );
};
